// Notifications: list, mark as read (single row and batch), realtime subscription,
// and resolution of the element a notification points at (EF-013).
//
// Every write goes through PostgREST with the caller session: the notifications_update
// policy narrows the statement to `destinataire_id = fn_uid()`, so an advisor can only
// flip their own rows even when an identifier from another perimeter is supplied.
import type { Notification } from '@horizon/types';
import { getClient } from './client.js';
import { toErreur, withRetry } from './erreurs.js';

const COLONNES = 'id,destinataire_id,titre,corps,lu,cree_le';

export async function listerNotifications(): Promise<readonly Notification[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('notifications')
      .select(COLONNES)
      .order('cree_le', { ascending: false })
      .limit(50);
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []) as unknown as Notification[];
  });
}

/** Mark one notification as read. No retry: a mutation is only sent once. */
export async function marquerLue(id: string): Promise<void> {
  const { error } = await getClient().from('notifications').update({ lu: true }).eq('id', id);
  if (error !== null) {
    throw toErreur(error);
  }
}

/**
 * Mark several notifications as read in a single statement, for the "Tout marquer lu"
 * action of the panel. Rows already read are excluded so the statement stays idempotent,
 * and rows outside the caller perimeter are removed by row level security instead of
 * raising: the returned identifiers are the ones the server actually flipped.
 */
export async function marquerLuesEnLot(ids: readonly string[]): Promise<readonly string[]> {
  if (ids.length === 0) {
    return [];
  }
  const { data, error } = await getClient()
    .from('notifications')
    .update({ lu: true })
    .in('id', [...ids])
    .eq('lu', false)
    .select('id');
  if (error !== null) {
    throw toErreur(error);
  }
  const lignes = (data ?? []) as unknown as readonly { id: string }[];
  return lignes.map((ligne) => ligne.id);
}

/** Subscribe to new notifications for the current user; returns an unsubscribe function. */
export function sAbonnerNotifications(
  destinataireId: string,
  surInsertion: (n: Notification) => void,
): () => void {
  const canal = getClient()
    .channel(`notifications:${destinataireId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `destinataire_id=eq.${destinataireId}`,
      },
      (message) => {
        surInsertion(message.new as unknown as Notification);
      },
    )
    .subscribe();
  return () => {
    void getClient().removeChannel(canal);
  };
}

/** Kind of element a notification designates. */
export type GenreCibleNotification = 'document' | 'client';

/** Element designated by a notification, resolved against real rows read under RLS. */
export interface CibleNotification {
  readonly genre: GenreCibleNotification;
  /** Identifier of the designated row. */
  readonly id: string;
  /** Client owning the designated row. */
  readonly clientId: string;
  /** French label of the element, used for the accessible name of the row. */
  readonly libelle: string;
}

interface LigneDocumentCible {
  readonly id: string;
  readonly client_id: string;
  readonly chemin_storage: string;
}

interface LigneClientCible {
  readonly id: string;
  readonly prenom: string;
  readonly nom: string;
}

/** Case and accent insensitive form, so "Hélène Moreau" matches "helene moreau". */
function normaliser(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/** Last segment of a storage path, that is the report file name shown to the advisor. */
function nomFichier(chemin: string): string {
  const segments = chemin.split('/');
  return segments[segments.length - 1] ?? chemin;
}

/** Reports of the caller perimeter, newest first, reduced to what the match needs. */
async function documentsDesignables(): Promise<readonly LigneDocumentCible[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('documents')
      .select('id,client_id,chemin_storage')
      .order('cree_le', { ascending: false })
      .limit(200);
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []) as unknown as LigneDocumentCible[];
  });
}

/** Active clients of the caller perimeter, reduced to what the match needs. */
async function clientsDesignables(): Promise<readonly LigneClientCible[]> {
  return withRetry(async () => {
    const { data, error } = await getClient()
      .from('clients')
      .select('id,prenom,nom')
      .eq('archive', false)
      .order('nom', { ascending: true });
    if (error !== null) {
      throw toErreur(error);
    }
    return (data ?? []) as unknown as LigneClientCible[];
  });
}

/** Candidate label to search inside a notification, longest first to avoid a prefix win. */
interface Candidat {
  readonly cible: CibleNotification;
  readonly cle: string;
}

function candidats(
  documents: readonly LigneDocumentCible[],
  clients: readonly LigneClientCible[],
): readonly Candidat[] {
  const listeDocuments = documents.map((document): Candidat => {
    const libelle = nomFichier(document.chemin_storage);
    return {
      cible: { genre: 'document', id: document.id, clientId: document.client_id, libelle },
      cle: normaliser(libelle),
    };
  });
  const listeClients = clients.map((client): Candidat => {
    const libelle = `${client.prenom} ${client.nom}`;
    return {
      cible: { genre: 'client', id: client.id, clientId: client.id, libelle },
      cle: normaliser(libelle),
    };
  });
  // A report name carries the client name, so documents are tried first; inside each
  // family the longest label wins, which keeps "Marie Dupont (2025).pdf" ahead of
  // "Marie Dupont.pdf" when both could match.
  const parLongueur = (a: Candidat, b: Candidat): number => b.cle.length - a.cle.length;
  return [...listeDocuments].sort(parLongueur).concat([...listeClients].sort(parLongueur));
}

/**
 * Resolve, for each notification, the element its wording designates. Nothing is
 * inferred from the wording alone: a notification only gets a target when a real row
 * readable by the caller carries a label quoted by the notification, so a notification
 * such as "3 versements du jour à valider" simply has none.
 */
export async function resoudreCiblesNotifications(
  notifications: readonly Notification[],
): Promise<ReadonlyMap<string, CibleNotification>> {
  const cibles = new Map<string, CibleNotification>();
  if (notifications.length === 0) {
    return cibles;
  }
  const [documents, clients] = await Promise.all([documentsDesignables(), clientsDesignables()]);
  const liste = candidats(documents, clients);
  if (liste.length === 0) {
    return cibles;
  }
  for (const notification of notifications) {
    const texte = normaliser(`${notification.titre} ${notification.corps}`);
    const trouve = liste.find(
      (candidat) => candidat.cle.length > 0 && texte.includes(candidat.cle),
    );
    if (trouve !== undefined) {
      cibles.set(notification.id, trouve.cible);
    }
  }
  return cibles;
}
